import React, { useEffect } from 'react';
import { Download, Terminal, Monitor, ArrowRight, Menu } from 'lucide-react';

let logo;
try {
  logo = require('./images/logo.png');
} catch (e) {
  logo = null;
}

const DownloadSoon = () => {
  useEffect(() => {
    const htmlElement = document.documentElement;
    const bodyElement = document.body;
    const rootElement = document.getElementById('root');

    const originalHtmlOverflow = htmlElement.style.overflow;
    const originalBodyOverflow = bodyElement.style.overflow;
    const originalRootOverflow = rootElement ? rootElement.style.overflow : '';

    htmlElement.style.overflow = 'auto';
    bodyElement.style.overflow = 'auto';

    const savedTheme = htmlElement.getAttribute('data-theme') || 'dark';
    htmlElement.setAttribute('data-theme', 'dark');
    bodyElement.setAttribute('data-theme', 'dark');

    return () => {
      htmlElement.style.overflow = originalHtmlOverflow;
      bodyElement.style.overflow = originalBodyOverflow;
      htmlElement.setAttribute('data-theme', savedTheme);
      bodyElement.setAttribute('data-theme', savedTheme);
    };
  }, []);

  return (
    <div className="hero-ref-bg text-slate-100 min-h-screen relative page-transition">
      {/* Ambient glows matching landing page */}
      <div className="absolute top-10 left-1/4 w-[500px] h-[300px] bg-blue-600/10 blur-[100px] rounded-full pointer-events-none" />
      <div className="absolute top-20 right-10 w-[600px] h-[400px] bg-cyan-500/10 blur-[120px] rounded-full pointer-events-none" />

      {/* Navigation — same as landing page */}
      <nav className="fixed top-0 w-full header-ref-nav z-50 transition-colors">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-14 sm:h-16">
            {/* Left Brand: elinnos | chipdesigner.ai */}
            <div className="flex items-center space-x-3">
              {logo ? (
                <a href="/" className="hover:opacity-80 transition-opacity flex items-center gap-2">
                  <img src={logo} alt="Elinnos" className="h-12 w-auto" />
                </a>
              ) : (
                <a href="/" className="text-xl font-extrabold tracking-tight text-white hover:text-cyan-400 transition-colors">
                  elinnos
                </a>
              )}
              <span className="text-slate-600 font-light text-lg">|</span>
              <a href="/" className="text-base sm:text-lg font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400 hover:opacity-80 transition-opacity">
                chipdesigner.ai
              </a>
            </div>

            {/* Center Navigation Links */}
            <div className="hidden md:flex items-center space-x-8">
              <a href="/#features" className="nav-tab">Features</a>
              <a href="/#demo" className="nav-tab">Platform</a>
              <a href="/dashboard" className="nav-tab">Dashboard</a>
              <a href="/#contact" className="nav-tab">Contact</a>
            </div>

            {/* Right User Actions */}
            <div className="hidden md:flex items-center space-x-4">
              <a
                href="/login"
                className="bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-500 hover:to-cyan-400 text-white px-5 py-2 rounded-lg text-sm font-medium shadow-[0_0_15px_rgba(6,182,212,0.3)] transition-all cursor-pointer"
                style={{ border: 'none' }}
              >
                Sign In
              </a>
            </div>

            <div className="md:hidden">
              <button className="text-slate-300 hover:text-white">
                <Menu className="w-6 h-6" />
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero-ref-bg circuit-trace-glow relative pt-28 sm:pt-32 pb-12 overflow-hidden">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="dl-fade-up inline-flex items-center gap-2 px-4 py-1.5 rounded-full dl-feature-pill mb-6">
            <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
            <span className="text-sm font-medium text-cyan-400">ChipDesigner IDE</span>
          </div>
          <h1 className="dl-fade-up-delay-1 text-5xl md:text-6xl font-bold tracking-tight text-white mb-6">
            Download <span className="gradient-text">ChipDesigner</span>
          </h1>
          <p className="dl-fade-up-delay-2 text-lg md:text-xl text-slate-400 max-w-2xl mx-auto leading-relaxed">
            Professional VLSI design environment with AI-powered assistance,
            waveform viewer, and integrated simulation tools.
          </p>
        </div>
      </section>

      {/* Coming Soon Cards */}
      <section className="py-12 relative">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="dl-fade-up-delay-2 text-center mb-8">
            <h2 className="text-2xl font-bold text-white mb-3">Downloads Available Soon</h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Binary installers for Linux and Windows will be available here once published.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Linux — Coming Soon */}
            <div className="dl-card dl-fade-up-delay-2 rounded-3xl p-8">
              <div className="flex items-center gap-4 mb-6">
                <div className="dl-icon-badge w-14 h-14 rounded-2xl flex items-center justify-center"
                  style={{ background: 'linear-gradient(135deg, rgba(249, 115, 22, 0.12), rgba(234, 88, 12, 0.08))' }}>
                  <Terminal className="w-7 h-7 text-orange-400" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-white">Linux</h2>
                  <p className="text-sm text-slate-400">Debian, Ubuntu, and derivatives</p>
                </div>
              </div>
              <div className="flex items-center justify-center py-4 px-8 rounded-2xl bg-slate-800/40 border border-slate-700/40">
                <span className="text-sm font-medium text-slate-400">Coming soon</span>
              </div>
            </div>

            {/* Windows — Coming Soon */}
            <div className="dl-card dl-fade-up-delay-3 rounded-3xl p-8">
              <div className="flex items-center gap-4 mb-6">
                <div className="dl-icon-badge w-14 h-14 rounded-2xl flex items-center justify-center"
                  style={{ background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.12), rgba(79, 70, 229, 0.08))' }}>
                  <Monitor className="w-7 h-7 text-blue-400" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-white">Windows</h2>
                  <p className="text-sm text-slate-400">Windows 10 and 11 (x64)</p>
                </div>
              </div>
              <div className="flex items-center justify-center py-4 px-8 rounded-2xl bg-slate-800/40 border border-slate-700/40">
                <span className="text-sm font-medium text-slate-400">Coming soon</span>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="dl-fade-up-delay-4 text-center mt-10">
            <p className="text-sm text-slate-400">
              Want to get notified when downloads are available?{' '}
              <a href="/#contact" className="text-cyan-400 hover:text-cyan-300 font-medium inline-flex items-center gap-1">
                Contact us <ArrowRight className="w-3.5 h-3.5" />
              </a>
            </p>
          </div>
        </div>
      </section>

      {/* Footer — same as landing page */}
      <footer className="border-t border-cyan-500/10 text-white py-6 relative z-10" style={{ background: 'linear-gradient(to top, rgba(10, 16, 32, 0.8) 0%, rgba(10, 16, 32, 0.3) 70%, transparent 100%)' }}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              {logo && <img src={logo} alt="Elinnos" className="h-6 w-auto" />}
              <span className="text-sm font-bold gradient-text">chipdesigner.ai</span>
            </div>

            <div className="flex items-center gap-6">
              <a href="/#features" className="text-sm text-slate-400 hover:text-white transition-colors">Features</a>
              <a href="/#demo" className="text-sm text-slate-400 hover:text-white transition-colors">Demo</a>
              <a href="https://elinnos.com" target="_blank" rel="noopener noreferrer" className="text-sm text-slate-400 hover:text-white transition-colors">Elinnos</a>
              <a href="mailto:contact@chipdesigner.ai" className="text-sm text-slate-400 hover:text-white transition-colors">Contact</a>
            </div>
          </div>

          <div className="border-t border-cyan-500/10 mt-4 pt-3 text-center">
            <p className="text-xs text-slate-500">&copy; 2026 chipdesigner.ai. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default DownloadSoon;
